
import { KnowledgeEntry } from "../types";
import { MetadataStatusBadges } from "./MetadataStatusBadges";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Tag, FileText, ScrollText, CalendarClock, Database, AlertTriangle, StickyNote } from "lucide-react";
import { KnowledgeEntryErrorHelp } from "../KnowledgeEntryErrorHelp";

interface MetadataDetailsViewProps {
  entry: KnowledgeEntry;
}

export function MetadataDetailsView({ entry }: MetadataDetailsViewProps) {
  // Add detailed logging of the metadata
  console.log("MetadataDetailsView: Received entry", {
    entryId: entry.id,
    hasMetadata: !!entry.metadata,
    metadataKeys: entry.metadata ? Object.keys(entry.metadata) : [],
    metadata: entry.metadata
  });

  // Add detailed logging of the entry metadata
  console.log("MetadataDetailsView: Component rendered with entry:", {
    entryId: entry.id,
    metadataType: typeof entry.metadata,
    hasTitle: !!entry.metadata?.generated_title,
    hasTags: !!entry.metadata?.generated_tags,
    hasSummary: !!entry.metadata?.summary,
    fullMetadata: entry.metadata,
    metadataKeys: entry.metadata ? Object.keys(entry.metadata) : []
  });

  // Helper function to format dates nicely
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };
  
  // Determine if the entry has AI-generated content
  const hasGeneratedTitle = !!entry.metadata?.generated_title;
  const hasGeneratedTags = entry.metadata?.generated_tags && 
    Array.isArray(entry.metadata.generated_tags) ? 
      entry.metadata.generated_tags.length > 0 : 
      !!entry.metadata.generated_tags;
  const hasSummary = !!entry.metadata?.summary;
  
  // Parse generated tags if they exist
  let generatedTags: string[] = [];
  if (entry.metadata?.generated_tags) {
    if (Array.isArray(entry.metadata.generated_tags)) {
      generatedTags = entry.metadata.generated_tags;
    } else if (typeof entry.metadata.generated_tags === 'string') {
      try {
        // Try to parse it if it's a JSON string
        generatedTags = JSON.parse(entry.metadata.generated_tags as string);
      } catch (e) {
        // If parsing fails, try to split it as a comma-separated string
        const tagsString = entry.metadata.generated_tags as string;
        generatedTags = tagsString.split(',').map(tag => tag.trim()).filter(Boolean);
      }
    }
  }

  // Check if there's an OpenAI API error
  const hasOpenAIError = 
    entry.metadata?.processing_error && 
    typeof entry.metadata.processing_error === 'string' && 
    entry.metadata.processing_error.includes('OpenAI API error');

  // Format the OpenAI API error message for display
  const formatOpenAIError = (errorMessage: string | Record<string, any>): string => {
    if (typeof errorMessage === 'string') {
      // Try to extract the JSON part of the error message
      const jsonMatch = errorMessage.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        try {
          const errorJson = JSON.parse(jsonMatch[0]);
          return JSON.stringify(errorJson, null, 2);
        } catch (e) {
          // If parsing fails, return the original message
          return errorMessage;
        }
      }
      return errorMessage;
    }
    
    return JSON.stringify(errorMessage, null, 2);
  };

  // Check if we have any metadata to display
  const hasMetadata = entry.metadata && (
    entry.metadata.generated_title ||
    entry.metadata.generated_tags ||
    entry.metadata.summary
  );

  return (
    <div className="space-y-6">
      <MetadataStatusBadges entry={entry} />
      
      {/* User Notes Section - only show if notes exist */}
      {entry.notes && (
        <div className="space-y-3 border rounded-md p-4 bg-secondary/10">
          <h3 className="text-lg font-medium flex items-center gap-2 border-b pb-2">
            <StickyNote className="h-5 w-5 text-secondary" />
            <span>Additional Notes</span>
          </h3>
          <ScrollArea className="h-auto max-h-48 w-full">
            <p className="text-sm whitespace-pre-wrap p-2">{entry.notes}</p>
          </ScrollArea>
        </div>
      )}
      
      {/* AI-Generated Metadata Section */}
      <div className="space-y-4 border rounded-md p-4 bg-muted/30">
        <h3 className="text-lg font-medium border-b pb-2">AI-Generated Metadata</h3>
        
        {hasOpenAIError && (
          <div className="mb-4 space-y-2 p-3 rounded border border-destructive/50 bg-destructive/10">
            <div className="flex items-center gap-2 text-destructive font-semibold">
              <AlertTriangle className="h-4 w-4" />
              <span>OpenAI API Error Detected</span>
            </div>
            <ScrollArea className="h-40 w-full rounded bg-black/10 p-2">
              <pre className="text-xs text-destructive font-mono whitespace-pre-wrap">
                {formatOpenAIError(entry.metadata.processing_error)}
              </pre>
            </ScrollArea>
          </div>
        )}
        
        {/* Title Section */}
        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <FileText className={`h-4 w-4 ${hasGeneratedTitle ? 'text-primary' : 'text-muted-foreground'}`} />
            <span className={`font-semibold ${hasGeneratedTitle ? '' : 'text-muted-foreground'}`}>
              {hasGeneratedTitle ? 'Generated Title:' : 'No Generated Title'}
            </span>
          </div>
          {hasGeneratedTitle ? (
            <p className="bg-muted p-2.5 rounded-md text-sm">{entry.metadata?.generated_title as string}</p>
          ) : (
            <p className="bg-muted/50 p-2.5 rounded-md text-sm italic text-muted-foreground">
              No AI-generated title available for this entry.
            </p>
          )}
        </div>
        
        {/* Tags Section */}
        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <Tag className={`h-4 w-4 ${hasGeneratedTags && generatedTags.length > 0 ? 'text-primary' : 'text-muted-foreground'}`} />
            <span className={`font-semibold ${hasGeneratedTags && generatedTags.length > 0 ? '' : 'text-muted-foreground'}`}>
              {hasGeneratedTags && generatedTags.length > 0 ? 'Generated Tags:' : 'No Generated Tags'}
            </span>
          </div>
          {hasGeneratedTags && generatedTags.length > 0 ? (
            <div className="flex flex-wrap gap-2 p-2.5 bg-muted rounded-md">
              {generatedTags.map((tag, idx) => (
                <Badge key={idx} variant="secondary" className="flex items-center gap-1">
                  <Tag className="h-3 w-3" />
                  {tag}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="bg-muted/50 p-2.5 rounded-md text-sm italic text-muted-foreground">
              No AI-generated tags available for this entry.
            </p>
          )}
        </div>
        
        {/* Summary Section */}
        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <ScrollText className={`h-4 w-4 ${hasSummary ? 'text-primary' : 'text-muted-foreground'}`} />
            <span className={`font-semibold ${hasSummary ? '' : 'text-muted-foreground'}`}>
              {hasSummary ? 'Summary:' : 'No Summary'}
            </span>
          </div>
          {hasSummary ? (
            <ScrollArea className="h-36 border rounded-md bg-muted">
              <p className="p-2.5 text-sm whitespace-pre-wrap">{entry.metadata?.summary as string}</p>
            </ScrollArea>
          ) : (
            <p className="bg-muted/50 p-2.5 rounded-md text-sm italic text-muted-foreground">
              No AI-generated summary available for this entry.
            </p>
          )}
        </div>
      </div>
      
      {/* Processing Details Section */}
      <div className="space-y-3 border rounded-md p-4">
        <h3 className="text-lg font-medium border-b pb-2">Processing Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
          <div>
            <span className="font-semibold">Status:</span> {entry.embedding_status}
          </div>
          <div>
            <span className="font-semibold">Last Updated:</span> {formatDate(entry.embedding_updated_at)}
          </div>
          {entry.metadata?.processing_timestamp && (
            <div>
              <span className="font-semibold">Processed At:</span> {formatDate(entry.metadata.processing_timestamp as string)}
            </div>
          )}
          {entry.metadata?.vector_store_id && (
            <div className="flex items-center gap-1">
              <span className="font-semibold">Vector Store ID:</span> 
              <span className="truncate max-w-[200px]">{entry.metadata.vector_store_id as string}</span>
            </div>
          )}
          {entry.metadata?.original_filename && (
            <div>
              <span className="font-semibold">Original Filename:</span> {entry.metadata.original_filename as string}
            </div>
          )}
        </div>
      </div>
      
      {/* Error Details Section */}
      {(entry.embedding_error || entry.metadata?.processing_error) && (
        <div className="space-y-3 border border-destructive rounded-md p-4">
          <h3 className="text-lg font-medium text-destructive border-b border-destructive/30 pb-2">Error Details</h3>
          
          {entry.embedding_error && (
            <div className="space-y-1">
              <span className="font-semibold text-destructive">Embedding Error:</span>
              <ScrollArea className="h-24 bg-destructive/5 p-2 rounded border border-destructive/30">
                <p className="text-sm text-destructive">{entry.embedding_error}</p>
              </ScrollArea>
            </div>
          )}
          
          {entry.metadata?.processing_error && !hasOpenAIError && (
            <div className="space-y-1">
              <span className="font-semibold text-destructive">Processing Error:</span>
              <ScrollArea className="h-24 bg-destructive/5 p-2 rounded border border-destructive/30">
                <pre className="text-sm text-destructive font-mono whitespace-pre-wrap">
                  {typeof entry.metadata.processing_error === 'string' 
                    ? entry.metadata.processing_error 
                    : JSON.stringify(entry.metadata.processing_error, null, 2)}
                </pre>
              </ScrollArea>
            </div>
          )}
          
          <KnowledgeEntryErrorHelp 
            errorMessage={
              entry.embedding_error || 
              (typeof entry.metadata?.processing_error === 'string' 
                ? entry.metadata.processing_error 
                : JSON.stringify(entry.metadata?.processing_error, null, 2))
            }
            originalFilename={entry.original_filename}
            metadata={entry.metadata}
          />
        </div>
      )}
    </div>
  );
}
